import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExternalLink, 
  Eye, 
  ArrowRight,
  TrendingUp,
  Users,
  Target,
  Code
} from 'lucide-react';
import Button from '../components/Button';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'digital-marketing', label: 'Digital Marketing' },
    { id: 'web-development', label: 'Web Development' },
    { id: 'social-media', label: 'Social Media' },
    { id: 'ecommerce', label: 'E-commerce' }
  ];

  const projects = [
    {
      id: 1,
      title: 'TechCorp Digital Transformation',
      category: 'digital-marketing',
      description: 'Complete digital marketing overhaul for a B2B tech company, resulting in 300% increase in qualified leads.',
      image: '/api/placeholder/600/400',
      tags: ['SEO', 'Content Marketing', 'Lead Generation'],
      results: {
        leads: '+300%',
        traffic: '+250%',
        conversions: '+180%'
      },
      client: 'TechCorp Inc.',
      industry: 'Technology',
      duration: '6 months'
    },
    {
      id: 2,
      title: 'EcoBrand E-commerce Platform',
      category: 'web-development',
      description: 'Custom e-commerce solution with advanced features, mobile optimization, and seamless user experience.',
      image: '/api/placeholder/600/400',
      tags: ['E-commerce', 'React', 'Mobile-First'],
      results: {
        sales: '+400%',
        mobile: '+95%',
        speed: '2.1s'
      },
      client: 'EcoBrand',
      industry: 'Retail',
      duration: '4 months'
    },
    {
      id: 3,
      title: 'StartupXYZ Social Media Campaign',
      category: 'social-media',
      description: 'Comprehensive social media strategy that built a strong community and increased brand awareness by 500%.',
      image: '/api/placeholder/600/400',
      tags: ['Social Media', 'Community Building', 'Brand Awareness'],
      results: {
        followers: '+500%',
        engagement: '+350%',
        reach: '+280%'
      },
      client: 'StartupXYZ',
      industry: 'Startup',
      duration: '3 months'
    },
    {
      id: 4,
      title: 'HealthPlus Medical Website',
      category: 'web-development',
      description: 'Professional medical website with patient portal, appointment booking, and HIPAA compliance.',
      image: '/api/placeholder/600/400',
      tags: ['Healthcare', 'Patient Portal', 'HIPAA'],
      results: {
        appointments: '+200%',
        patients: '+150%',
        satisfaction: '98%'
      },
      client: 'HealthPlus Medical',
      industry: 'Healthcare',
      duration: '5 months'
    },
    {
      id: 5,
      title: 'FashionForward Meta Ads',
      category: 'digital-marketing',
      description: 'High-converting Facebook and Instagram ad campaigns that drove significant ROI for a fashion brand.',
      image: '/api/placeholder/600/400',
      tags: ['Meta Ads', 'Facebook', 'Instagram'],
      results: {
        roas: '4.2x',
        cpa: '-60%',
        revenue: '+350%'
      },
      client: 'FashionForward',
      industry: 'Fashion',
      duration: '4 months'
    },
    {
      id: 6,
      title: 'FoodieHub Restaurant Platform',
      category: 'ecommerce',
      description: 'Multi-restaurant food delivery platform with real-time tracking and seamless payment integration.',
      image: '/api/placeholder/600/400',
      tags: ['Food Delivery', 'Real-time', 'Payment'],
      results: {
        orders: '+600%',
        restaurants: '+200',
        users: '+150%'
      },
      client: 'FoodieHub',
      industry: 'Food & Beverage',
      duration: '8 months'
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '250%', label: 'Average ROI' },
    { number: '50+', label: 'Happy Clients' }
  ];

  return (
    <>
      <Helmet>
        <title>Portfolio - Our Work & Case Studies | Subtronz</title>
        <meta name="description" content="Explore our portfolio of successful digital marketing and web development projects. See real results and case studies from our satisfied clients." />
        <meta name="keywords" content="portfolio, case studies, digital marketing projects, web development examples, client work" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-50 to-purple-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-secondary mb-6">
              Our <span className="gradient-text">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Explore our successful projects and see how we've helped businesses 
              achieve their digital marketing and web development goals.
            </p>
            <Button size="lg" href="/contact">
              Start Your Project
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4"
          >
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-primary hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative overflow-hidden">
                    <div className="w-full h-48 bg-gradient-to-br from-primary/20 to-blue-600/20 flex items-center justify-center">
                      <div className="text-6xl text-primary/30">
                        {project.category === 'digital-marketing' && <TrendingUp />}
                        {project.category === 'web-development' && <Code />}
                        {project.category === 'social-media' && <Users />}
                        {project.category === 'ecommerce' && <Target />}
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="flex space-x-4">
                        <button className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200">
                          <Eye className="w-5 h-5" />
                        </button>
                        <button className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200">
                          <ExternalLink className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <h3 className="text-xl font-bold text-secondary mb-2">{project.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {Object.entries(project.results).map(([key, value], resultIndex) => (
                        <div key={resultIndex} className="text-center">
                          <div className="text-lg font-bold text-primary">{value}</div>
                          <div className="text-xs text-gray-500 capitalize">{key}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                      <span>{project.client}</span>
                      <span>{project.duration}</span>
                    </div>
                    
                    <Button variant="outline" size="sm" className="w-full">
                      View Case Study
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson',
                role: 'Marketing Director',
                company: 'TechCorp',
                content: 'Subtronz transformed our digital presence completely. Our ROI increased by 300% in just 6 months!',
                rating: 5
              },
              {
                name: 'Mike Chen',
                role: 'CEO',
                company: 'StartupXYZ',
                content: 'The team at Subtronz is incredible. They delivered exactly what they promised and more.',
                rating: 5
              },
              {
                name: 'Emily Rodriguez',
                role: 'Founder',
                company: 'EcoBrand',
                content: 'Professional, creative, and results-driven. Subtronz helped us scale our business online.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <div key={i} className="w-4 h-4 text-yellow-400">★</div>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-secondary">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role} at {testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary via-accent to-purple">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Let's discuss your project and see how we can help you achieve similar results.
            </p>
            <Button variant="secondary" size="lg" href="/contact#contact-form">
              Get Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Portfolio;
